<template>
  <CPreloader v-if="!isFirstBlockReady" />

  <template v-else>
    <CDetailPageHeader
      :breadcrumbs-items="breadcrumbsItems"
      :title="
        machine
          ? `Автомат${machine?.attributes.erp_id ? ` ${machine?.attributes.erp_id}` : ''}, ${machine?.attributes.address}`
          : ''
      "
    />

    <div
      v-if="machine"
      class="machine-view-flex"
    >
      <!-- Левая колонка с изображением -->
      <MachineImageCard
        :machine="machine"
        class="flex-1 machine-view-flex__item"
      />

      <!-- Правая колонка с картой -->
      <MapCard
        :highlighted-vending-machine-id="machine.id"
        :location="location"
      />
    </div>
  </template>

  <CCardWithBorder
    v-if="machine && (instructionImage || machine.attributes.instructions) && isFirstBlockReady"
  >
    <div class="machine-view-instructions">
      <div class="machine-view-instructions-text">
        <CMarkedText :text="machine.attributes.instructions" />
      </div>

      <img
        v-if="instructionImage"
        :src="instructionImage"
        alt="Machine Image"
        class="machine-view-instructions-image"
      />
    </div>
  </CCardWithBorder>

  <WrapperWithCard
    v-if="isFirstBlockReady"
    :enable-card="!isMobile"
    class="machine-view-products-wrapper"
  >
    <CategoriesList
      :categories="categories"
      :title="`Витрина Автомата ${machine?.attributes.erp_id}:`"
    >
      <template #products="{ category }">
        <MachineCategoryProducts
          :category="category"
          :machine-id="machine!.id"
        />
      </template>
    </CategoriesList>
  </WrapperWithCard>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue';
  import { useRoute } from 'vue-router';

  import { getVendingMachine, VendingMachine } from '@/api/requests/VendingMachines';
  import { getUploadUrl } from '@/helpers/urls';

  import CCardWithBorder from '@/components/Cards/CCardWithBorder.vue';
  import CDetailPageHeader from '@/components/CDetailPageHeader/CDetailPageHeader.vue';
  import CMarkedText from '@/components/CMarkedText/CMarkedText.vue';
  import { Product } from '@/services/product/types';
  import CPreloader from '@/components/CPreloader/CPreloader.vue';

  import { useYMaps } from '@/composables/useYMaps';
  import type { YMapLocationRequest } from '@yandex/ymaps3-types';
  import MapCard from '@/components/mainPage/MapCard.vue';
  import WrapperWithCard from '@/components/Cards/WrapperWithCard.vue';
  import { useClientSize } from '@/composables/useClientSize';
  import MachineImageCard from '@/components/machine/MachineImageCard.vue';
  import CategoriesList from '@/components/CategoriesList/CategoriesList.vue';
  import MachineCategoryProducts from '@/components/machine/MachineCategoryProducts.vue';
  import { Category, getCategoriesByMachineId } from '@/services/category/api';

  const { isReady: isYMapsReady, isLibError } = useYMaps();
  const route = useRoute();
  const { isMobile } = useClientSize();

  const machine = ref<VendingMachine | null>(null);

  const categories = ref<Category[] | null>(null);

  // TODO: добавить поддержку что может они не с главной пришли.
  // а откуда?
  // 1. c главной
  // 2. со страницы товара
  const breadcrumbsItems = computed(() => {
    const first = { title: 'Главная', link: '/?to=catalog' };

    if (!machine.value) {
      return [first];
    }

    const last = { title: machine.value?.attributes.address };

    return [first, last];
  });

  const isFirstBlockReady = computed(
    () => !!machine.value && !!(isYMapsReady.value || isLibError.value)
  );

  const instructionImage = computed(() => {
    const imageUrl = machine.value?.attributes.instruction_image?.data?.attributes.url;

    return imageUrl ? getUploadUrl(imageUrl) : null;
  });

  const location = computed<YMapLocationRequest | undefined>(() => {
    const coordinates = machine.value?.attributes.Coordinates;

    return coordinates
      ? {
          center: [coordinates.coordinates.lng, coordinates.coordinates.lat],
          zoom: 16
        }
      : undefined;
  });

  function getCategories() {
    getCategoriesByMachineId(route.params.id.toString()).then(response => {
      categories.value = response.data.data;
    });
  }

  onMounted(async () => {
    const { id } = route.params;

    async function loadMachine() {
      const response = await getVendingMachine(id.toString());

      machine.value = response.data.data;
    }

    getCategories();

    await Promise.all([loadMachine()]);
  });
</script>

<style>
  .machine-view-flex {
    display: flex;
    gap: 26px;
    overflow: hidden;
    margin-bottom: 26px;

    &__item {
      width: calc(50% - 13px);
    }
  }

  .flex-1 {
    flex: 1;
  }

  .machine-view-map {
    min-height: 500px;
  }

  .machine-view-instructions {
    display: flex;
    flex-direction: row;
    gap: 26px;
    padding: 49px 49px 25px 49px;
  }

  .machine-view-instructions-image {
    width: 310px;
    height: auto;
    object-fit: cover;
    object-position: center;
    display: block;
  }

  .machine-view-products {
    padding: 24px 40px;

    &-wrapper {
      margin-top: 24px;
    }
  }

  @media (--mobile) {
    .machine-view-flex {
      flex-direction: column;
      gap: 24px;
      margin-bottom: 24px;

      &__item {
        width: auto;
      }
    }

    .machine-view-products {
      padding: 24px 12px;
    }

    .machine-view-instructions {
      flex-direction: column;
      padding: 24px 10px 0;
      align-items: center;
    }
  }
</style>
